import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import HelpTooltip from '../../components/UI/HelpTooltip';
import { websiteSettingsAPI } from '../../services/api';
import { 
  Settings, Save, RefreshCw, Database, Shield, Globe, 
  Image, Upload, X, CheckCircle, AlertCircle, Monitor 
} from 'lucide-react';

const RAW_API_URL = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api').replace(/\/+$/, '');
const API_ORIGIN = RAW_API_URL.endsWith('/api') ? RAW_API_URL.slice(0, -4) : RAW_API_URL;

const buildMediaUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const cleaned = path.replace(/^\/+/, '').replace(/^storage\//, '').replace(/^public\//, '');
  return `${API_ORIGIN}/storage/${cleaned}`;
};

const extractPathFromUrl = (url) => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const storageIndex = parsed.pathname.indexOf('/storage/');
    if (storageIndex >= 0) {
      return parsed.pathname.slice(storageIndex + '/storage/'.length);
    }
    return parsed.pathname.replace(/^\/+/, '');
  } catch {
    return null;
  }
};

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const SettingsCard = styled(Card)`
  padding: 2rem;
`;

const SettingsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SettingsIcon = styled.div`
  width: 50px;
  height: 50px;
  background-color: ${props => props.theme.colors.primary + '20'};
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.primary};
`;

const SettingsTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const FileUploadArea = styled.div`
  border: 2px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.primary + '05'};
  }
`;

const ImagePreview = styled.div`
  position: relative;
  display: inline-block;
  margin-top: 1rem;
  
  img {
    max-width: 200px;
    max-height: 200px;
    border-radius: ${props => props.theme.borderRadius.md};
    border: 1px solid ${props => props.theme.colors.border};
  }
`;

const DeleteButton = styled.button`
  position: absolute;
  top: -10px;
  right: -10px;
  background: ${props => props.theme.colors.error};
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${props => props.theme.colors.errorDark};
  }
`;

const ModeToggleGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const ModeOption = styled.button`
  padding: 0.65rem 1.25rem;
  border-radius: ${props => props.theme.borderRadius.full};
  border: 1px solid ${props => props.$active ? props.theme.colors.primary : props.theme.colors.border};
  background: ${props => props.$active ? props.theme.colors.primary + '10' : 'transparent'};
  color: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.text.secondary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const BackgroundGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
`;

const BackgroundTile = styled.button`
  position: relative;
  padding: 0;
  border: 2px solid ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  height: 110px;
  cursor: pointer;
  background-image: ${props => props.$image ? `url(${props.$image})` : 'linear-gradient(135deg, #e5e5e5, #f7f7f7)'};
  background-size: cover;
  background-position: center;
  box-shadow: 0 12px 24px rgba(0,0,0,0.15);
  transition: transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: ${props => props.theme.colors.primary};
  }
`;

const TileOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: ${props => props.$active ? 'linear-gradient(135deg, rgba(0,0,0,0.65), rgba(0,0,0,0.2))' : 'linear-gradient(135deg, rgba(0,0,0,0.4), rgba(0,0,0,0.1))'};
`;

const ActiveBadge = styled.span`
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  padding: 0.15rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  background: rgba(255, 255, 255, 0.9);
  color: ${props => props.theme.colors.primary};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TileDeleteButton = styled.button`
  position: absolute;
  top: 0.35rem;
  right: 0.35rem;
  border: none;
  border-radius: ${props => props.theme.borderRadius.full};
  background: rgba(0, 0, 0, 0.45);
  color: white;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.75);
  }
`;

const EmptyBackgroundState = styled.div`
  border: 1px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 1.5rem;
  text-align: center;
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const ProgressPreview = styled.div`
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: ${props => props.theme.colors.background.secondary};
`;

const PreviewTrack = styled.div`
  width: 100%;
  height: ${props => props.$style === 'thin' ? '6px' : props.$style === 'flat' ? '12px' : '16px'};
  border-radius: ${props => props.$style === 'flat' ? props.theme.borderRadius.md : props.theme.borderRadius.full};
  background: ${props => props.$track};
  overflow: hidden;
`;

const PreviewFill = styled.div`
  width: 60%;
  height: 100%;
  border-radius: inherit;
  background: ${props => props.$color};
`;

const Message = styled.div`
  padding: 0.75rem 1rem;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${props => props.type === 'success' && `
    background-color: #d4edda;
    border: 1px solid #c3e6cb;
    color: #155724;
  `}
  
  ${props => props.type === 'error' && `
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;
  `}
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.typography.fontSize.base};
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const SettingsPage = () => {
  const { theme, updateBrandingColors } = useTheme();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  
  const [websiteSettings, setWebsiteSettings] = useState({
    website_name: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_title: '',
    og_description: '',
    twitter_card_type: 'summary_large_image',
    twitter_site: '',
    logo_path: null,
    favicon_path: null,
    og_image_path: null,
    primary_color: '#9333EA',
    primary_dark_color: '#7C3AED',
    primary_light_color: '#A855F7',
    login_background_mode: 'gradient',
    login_active_background: '',
    login_progress_color: '#ffffff',
    login_progress_track_color: 'rgba(255,255,255,0.35)',
    login_progress_style: 'pill',
    login_background_gallery: [],
    login_background_images: [],
    login_active_background_url: null,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await websiteSettingsAPI.getSettings();
      if (response.data) {
        console.log('Website settings fetched:', response.data);
        setWebsiteSettings({
          ...response.data,
          // URLs are already full URLs from the backend
        });
      }
    } catch (error) {
      console.error('Error fetching website settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loginBackgroundGallery = useMemo(() => {
    const galleryItems = Array.isArray(websiteSettings.login_background_gallery)
      ? websiteSettings.login_background_gallery
      : [];

    const normalized = galleryItems
      .map(item => {
        const path = item?.path || extractPathFromUrl(item?.url);
        const url = item?.url || buildMediaUrl(path);
        return { path, url };
      })
      .filter(item => item.url);

    if (normalized.length) return normalized;

    if (Array.isArray(websiteSettings.login_background_images)) {
      return websiteSettings.login_background_images
        .map(path => ({
          path,
          url: buildMediaUrl(path),
        }))
        .filter(item => item.url);
    }

    if (websiteSettings.login_active_background_url) {
      const derivedPath = websiteSettings.login_active_background || extractPathFromUrl(websiteSettings.login_active_background_url);
      return [{
        path: derivedPath,
        url: websiteSettings.login_active_background_url,
      }];
    }

    return [];
  }, [websiteSettings]);

  const loginBackgroundMode = websiteSettings.login_background_mode || (loginBackgroundGallery.length ? 'image' : 'gradient');

  const handleChange = (field, value) => {
    setWebsiteSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (field, file, uploadFunction) => {
    if (!file) return;
    
    const formData = new FormData();
    const fieldName = field === 'logo' ? 'logo' : field === 'favicon' ? 'favicon' : 'og_image';
    formData.append(fieldName, file);

    try {
      setLoading(true);
      setMessage(null);
      const response = await uploadFunction(formData);
      const pathField = field === 'logo' ? 'logo_path' : field === 'favicon' ? 'favicon_path' : 'og_image_path';
      // The backend returns the URL in the response
      const url = response.data[pathField] || (response.data.settings && response.data.settings[pathField]);
      
      if (url) {
        setWebsiteSettings(prev => ({ ...prev, [pathField]: url }));
        setMessage({ type: 'success', text: `${field} uploaded successfully!` });
        setTimeout(() => setMessage(null), 3000);
      } else {
        // Refresh settings after upload
        await fetchSettings();
        setMessage({ type: 'success', text: `${field} uploaded successfully!` });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      console.error(`Error uploading ${field}:`, error);
      const errorMessage = error.response?.data?.message || error.response?.data?.errors || `Failed to upload ${field}. Please try again.`;
      setMessage({ type: 'error', text: typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage) });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  const applySettingsFromResponse = (data) => {
    if (data?.settings) {
      setWebsiteSettings(prev => ({
        ...prev,
        ...data.settings,
      }));
    }
  };

  const handleDeleteImage = async (field, deleteFunction) => {
    try {
      setLoading(true);
      await deleteFunction();
      const pathField = field === 'logo' ? 'logo_path' : field === 'favicon' ? 'favicon_path' : 'og_image_path';
      setWebsiteSettings(prev => ({ ...prev, [pathField]: null }));
      setMessage({ type: 'success', text: `${field} deleted successfully!` });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error(`Error deleting ${field}:`, error);
      setMessage({ type: 'error', text: `Failed to delete ${field}. Please try again.` });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginBackgroundUpload = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('background', file);

    try {
      setLoading(true);
      setMessage(null);
      const response = await websiteSettingsAPI.uploadLoginBackground(formData);
      applySettingsFromResponse(response.data);
      setMessage({ type: 'success', text: 'Background uploaded successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error uploading login background:', error);
      const errorMessage = error.response?.data?.message || 'Failed to upload background. Please try again.';
      setMessage({ type: 'error', text: errorMessage });
      setTimeout(() => setMessage(null), 4000);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLoginBackground = async (path) => {
    if (!path) return;
    try {
      setLoading(true);
      setMessage(null);
      const response = await websiteSettingsAPI.deleteLoginBackground({ path });
      applySettingsFromResponse(response.data);
      setMessage({ type: 'success', text: 'Background removed successfully.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting login background:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete background.';
      setMessage({ type: 'error', text: errorMessage });
      setTimeout(() => setMessage(null), 4000);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLoginBackground = (background) => {
    if (!background) return;
    const resolvedPath = background.path || extractPathFromUrl(background.url);
    if (!resolvedPath) return;
    handleChange('login_active_background', resolvedPath);
    handleChange('login_background_mode', 'image');

    setWebsiteSettings(prev => ({
      ...prev,
      login_active_background_url: background.url || prev.login_active_background_url,
    }));

    setMessage({ type: 'success', text: 'Background selected. Click “Save Settings” to apply.' });
    setTimeout(() => setMessage(null), 3500);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setMessage(null);
      
      // Only send text fields, preserve image paths
      const {
        logo_path,
        favicon_path,
        og_image_path,
        login_background_gallery,
        login_background_images,
        login_active_background_url,
        ...settingsData
      } = websiteSettings;
      
      // Ensure login progress colors have defaults if not set
      if (!settingsData.login_progress_color) {
        settingsData.login_progress_color = '#ffffff';
      }
      if (!settingsData.login_progress_track_color) {
        settingsData.login_progress_track_color = 'rgba(255,255,255,0.35)';
      }
      if (!settingsData.login_progress_style) {
        settingsData.login_progress_style = 'pill';
      }
      
      console.log('Saving settings with data:', settingsData);
      const response = await websiteSettingsAPI.updateSettings(settingsData);
      
      // Update with response data to ensure we have the latest URLs
      if (response.data && response.data.settings) {
        setWebsiteSettings(prev => ({
          ...prev,
          ...response.data.settings
        }));
      }
      
      // Update theme colors immediately after save
      if (updateBrandingColors) {
        updateBrandingColors({
          primary: websiteSettings.primary_color || '#9333EA',
          primaryDark: websiteSettings.primary_dark_color || '#7C3AED',
          primaryLight: websiteSettings.primary_light_color || '#A855F7',
        });
      }
      
      // Trigger event to notify other components (like Login page) to refresh
      window.dispatchEvent(new CustomEvent('website-settings-updated', {
        detail: { settings: response.data.settings }
      }));
      
      setMessage({ type: 'success', text: 'Website settings saved successfully! Colors and login experience updated.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving website settings:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.errors || 'Failed to save settings. Please try again.';
      setMessage({ type: 'error', text: typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage) });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <SettingsContainer theme={theme}>
      <Title theme={theme}>
        Website Settings
        <HelpTooltip
          steps={[
            {
              title: 'Step 1 – Basic branding',
              content: (
                <ul>
                  <li>Set the website name, logo and favicon so the public site and emails carry the correct identity.</li>
                  <li>Choose primary colors that match your organization’s brand.</li>
                </ul>
              ),
            },
            {
              title: 'Step 2 – Login experience',
              content: (
                <ul>
                  <li>Configure the login background (gradient or image gallery) to make the staff portal welcoming.</li>
                  <li>Adjust the progress indicator style and colors for better usability.</li>
                </ul>
              ),
            },
            {
              title: 'Step 3 – SEO & social sharing',
              content: (
                <ul>
                  <li>Fill in meta title, description and keywords to help search engines understand your site.</li>
                </ul>
              ),
            },
          ]}
        />
      </Title>
      
      {message && (
        <Message type={message.type} theme={theme}>
          {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {message.text}
        </Message>
      )}

        <SettingsCard theme={theme}>
          <SettingsHeader theme={theme}>
            <SettingsIcon theme={theme}>
            <Globe size={24} />
            </SettingsIcon>
          <SettingsTitle theme={theme}>General Website Settings</SettingsTitle>
          </SettingsHeader>
          
          <FormGroup theme={theme}>
            <Input
            label="Website Name"
            value={websiteSettings.website_name || ''}
            onChange={(e) => handleChange('website_name', e.target.value)}
            placeholder="Enter website name"
            helpText="This will be displayed in the browser tab and emails"
          />
          
            <div>
              <label style={{ 
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.text.primary,
                marginBottom: '0.5rem',
                display: 'block'
              }}>
              Logo
              </label>
            {websiteSettings.logo_path ? (
              <ImagePreview theme={theme}>
                <img 
                  src={websiteSettings.logo_path} 
                  alt="Logo" 
                  onError={(e) => {
                    console.error('Error loading logo:', websiteSettings.logo_path);
                    e.target.style.display = 'none';
                  }}
                  onLoad={() => console.log('Logo loaded:', websiteSettings.logo_path)}
                />
                <DeleteButton 
                  theme={theme}
                  onClick={() => handleDeleteImage('logo', websiteSettingsAPI.deleteLogo)}
                  title="Delete logo"
                >
                  <X size={16} />
                </DeleteButton>
              </ImagePreview>
            ) : (
              <FileUploadArea 
                theme={theme}
                onClick={() => document.getElementById('logo-upload').click()}
              >
                <Upload size={32} style={{ marginBottom: '0.5rem', color: theme.colors.text.secondary }} />
                <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
                  Click to upload logo (PNG, JPG, GIF, SVG - Max 2MB)
                </p>
              <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleFileUpload('logo', file, websiteSettingsAPI.uploadLogo);
                  }}
                />
              </FileUploadArea>
            )}
            </div>
            
            <div>
              <label style={{ 
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.text.primary,
                marginBottom: '0.5rem',
                display: 'block'
              }}>
              Favicon
              </label>
            {websiteSettings.favicon_path ? (
              <ImagePreview theme={theme}>
                <img 
                  src={websiteSettings.favicon_path} 
                  alt="Favicon"
                  onError={(e) => {
                    console.error('Error loading favicon:', websiteSettings.favicon_path);
                    e.target.style.display = 'none';
                  }}
                  onLoad={() => console.log('Favicon loaded:', websiteSettings.favicon_path)}
                />
                <DeleteButton 
                  theme={theme}
                  onClick={() => handleDeleteImage('favicon', websiteSettingsAPI.deleteFavicon)}
                  title="Delete favicon"
                >
                  <X size={16} />
                </DeleteButton>
              </ImagePreview>
            ) : (
              <FileUploadArea 
                theme={theme}
                onClick={() => document.getElementById('favicon-upload').click()}
              >
                <Upload size={32} style={{ marginBottom: '0.5rem', color: theme.colors.text.secondary }} />
                <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
                  Click to upload favicon (ICO, PNG, JPG, GIF - Max 2MB)
                </p>
              <input
                  id="favicon-upload"
                  type="file"
                  accept=".ico,.png,.jpg,.jpeg,.gif"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleFileUpload('favicon', file, websiteSettingsAPI.uploadFavicon);
                  }}
                />
              </FileUploadArea>
            )}
            </div>
        </FormGroup>
      </SettingsCard>

      <SettingsCard theme={theme}>
        <SettingsHeader theme={theme}>
          <SettingsIcon theme={theme}>
            <Database size={24} />
          </SettingsIcon>
          <SettingsTitle theme={theme}>Branding Colors</SettingsTitle>
        </SettingsHeader>
        
        <FormGroup theme={theme}>
          <div>
            <label style={{ 
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.text.primary,
              marginBottom: '0.5rem',
              display: 'block'
            }}>
              Primary Color
            </label>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <input
                type="color"
                value={websiteSettings.primary_color || '#9333EA'}
                onChange={(e) => handleChange('primary_color', e.target.value)}
                style={{
                  width: '80px',
                  height: '40px',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.md,
                  cursor: 'pointer'
                }}
              />
              <Input
                value={websiteSettings.primary_color || '#9333EA'}
                onChange={(e) => handleChange('primary_color', e.target.value)}
                placeholder="#9333EA"
                style={{ flex: 1 }}
                helpText="Main brand color used throughout the site"
              />
            </div>
          </div>

          <div>
            <label style={{ 
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.text.primary,
              marginBottom: '0.5rem',
              display: 'block'
            }}>
              Primary Dark Color
            </label>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <input
                type="color"
                value={websiteSettings.primary_dark_color || '#7C3AED'}
                onChange={(e) => handleChange('primary_dark_color', e.target.value)}
                style={{
                  width: '80px',
                  height: '40px',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.md,
                  cursor: 'pointer'
                }}
              />
              <Input
                value={websiteSettings.primary_dark_color || '#7C3AED'}
                onChange={(e) => handleChange('primary_dark_color', e.target.value)}
                placeholder="#7C3AED"
                style={{ flex: 1 }}
                helpText="Darker shade for hover states and accents"
              />
            </div>
          </div>

          <div>
            <label style={{ 
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.text.primary,
              marginBottom: '0.5rem',
              display: 'block'
            }}>
              Primary Light Color
            </label>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <input
                type="color"
                value={websiteSettings.primary_light_color || '#A855F7'}
                onChange={(e) => handleChange('primary_light_color', e.target.value)}
                style={{
                  width: '80px',
                  height: '40px',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.md,
                  cursor: 'pointer'
                }}
              />
              <Input
                value={websiteSettings.primary_light_color || '#A855F7'}
                onChange={(e) => handleChange('primary_light_color', e.target.value)}
                placeholder="#A855F7"
                style={{ flex: 1 }}
                helpText="Lighter shade for backgrounds and highlights"
              />
            </div>
          </div>

          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: theme.colors.background.secondary,
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.colors.border.light}`
          }}>
            <p style={{ 
              fontSize: theme.typography.fontSize.sm, 
              color: theme.colors.text.secondary,
              margin: '0 0 0.5rem 0'
            }}>
              <strong>Color Preview:</strong>
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{
                width: '60px',
                height: '60px',
                backgroundColor: websiteSettings.primary_color || '#9333EA',
                borderRadius: theme.borderRadius.md,
                border: `1px solid ${theme.colors.border.light}`
              }} title="Primary" />
              <div style={{
                width: '60px',
                height: '60px',
                backgroundColor: websiteSettings.primary_dark_color || '#7C3AED',
                borderRadius: theme.borderRadius.md,
                border: `1px solid ${theme.colors.border.light}`
              }} title="Primary Dark" />
              <div style={{
                width: '60px',
                height: '60px',
                backgroundColor: websiteSettings.primary_light_color || '#A855F7',
                borderRadius: theme.borderRadius.md,
                border: `1px solid ${theme.colors.border.light}`
              }} title="Primary Light" />
            </div>
            <p style={{ 
              fontSize: theme.typography.fontSize.xs, 
              color: theme.colors.text.secondary,
              margin: '0.5rem 0 0 0',
              fontStyle: 'italic'
            }}>
              Note: Changes will take effect after saving and refreshing the page.
            </p>
          </div>
        </FormGroup>
      </SettingsCard>

      <SettingsCard theme={theme}>
        <SettingsHeader theme={theme}>
          <SettingsIcon theme={theme}>
            <Monitor size={24} />
          </SettingsIcon>
          <SettingsTitle theme={theme}>Login Experience</SettingsTitle>
        </SettingsHeader>

        <FormGroup theme={theme}>
          <div>
            <label style={{
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.text.primary,
              marginBottom: '0.5rem',
              display: 'block'
            }}>
              Background Style
            </label>
            <ModeToggleGroup>
              <ModeOption
                type="button"
                $active={loginBackgroundMode === 'gradient'}
                onClick={() => handleChange('login_background_mode', 'gradient')}
              >
                Gradient
              </ModeOption>
              <ModeOption
                type="button"
                $active={loginBackgroundMode === 'image'}
                onClick={() => handleChange('login_background_mode', 'image')}
              >
                Image Gallery
              </ModeOption>
            </ModeToggleGroup>
            <small style={{ color: theme.colors.text.secondary, display: 'block', marginTop: '0.35rem' }}>
              Switch between a brand gradient (uses your primary colors) or curated background images.
            </small>
          </div>

          {loginBackgroundMode === 'image' ? (
            <>
              <FileUploadArea
                theme={theme}
                onClick={() => document.getElementById('login-bg-upload').click()}
                style={{ marginTop: '0.5rem' }}
              >
                <Upload size={32} style={{ marginBottom: '0.5rem', color: theme.colors.text.secondary }} />
                <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
                  Upload background (JPG, PNG, WEBP - Max 5MB)
                </p>
                <input
                  id="login-bg-upload"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleLoginBackgroundUpload(file);
                  }}
                />
              </FileUploadArea>

              {loginBackgroundGallery.length > 0 ? (
                <>
                  <BackgroundGallery>
                    {loginBackgroundGallery.map((background, index) => {
                      const isActive =
                        (websiteSettings.login_active_background && background.path && websiteSettings.login_active_background === background.path) ||
                        (!websiteSettings.login_active_background && websiteSettings.login_active_background_url && background.url === websiteSettings.login_active_background_url);
                      
                      return (
                      <BackgroundTile
                        key={background.path || background.url || index}
                        $image={background.url}
                        $active={isActive}
                        onClick={() => handleSelectLoginBackground(background)}
                      >
                        <TileOverlay $active={isActive} />
                        {isActive && (
                          <ActiveBadge>Active</ActiveBadge>
                        )}
                        <TileDeleteButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLoginBackground(background.path);
                          }}
                          title="Remove background"
                        >
                          <X size={14} />
                        </TileDeleteButton>
                      </BackgroundTile>
                    )})}
                  </BackgroundGallery>
                  <small style={{ color: theme.colors.text.secondary }}>
                    Tip: Select a background above and click “Save Settings” to publish it to the login page.
                  </small>
                </>
              ) : (
                <EmptyBackgroundState theme={theme}>
                  No backgrounds uploaded yet. Upload an image to start customizing the login screen.
                </EmptyBackgroundState>
              )}
            </>
          ) : (
            <div style={{
              padding: '1rem',
              borderRadius: theme.borderRadius.md,
              border: `1px dashed ${theme.colors.border}`,
              background: theme.colors.background.secondary,
              color: theme.colors.text.secondary,
              fontSize: theme.typography.fontSize.sm
            }}>
              The login page will use a gradient that blends your primary brand colors (
              <strong>{websiteSettings.primary_dark_color}</strong> → <strong>{websiteSettings.primary_color}</strong>).
            </div>
          )}
        </FormGroup>

        <FormGroup theme={theme}>
          <div>
            <label style={{
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.text.primary,
              marginBottom: '0.5rem',
              display: 'block'
            }}>
              Login Progress Indicator
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <input
                  type="color"
                  value={websiteSettings.login_progress_color || '#ffffff'}
                  onChange={(e) => handleChange('login_progress_color', e.target.value)}
                  style={{
                    width: '60px',
                    height: '40px',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: theme.borderRadius.md,
                    cursor: 'pointer'
                  }}
                />
                <Input
                  label="Progress Color"
                  value={websiteSettings.login_progress_color || '#ffffff'}
                  onChange={(e) => handleChange('login_progress_color', e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <input
                  type="color"
                  value={websiteSettings.login_progress_track_color || '#d1d5db'}
                  onChange={(e) => handleChange('login_progress_track_color', e.target.value)}
                  style={{
                    width: '60px',
                    height: '40px',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: theme.borderRadius.md,
                    cursor: 'pointer'
                  }}
                />
                <Input
                  label="Track Color"
                  value={websiteSettings.login_progress_track_color || '#d1d5db'}
                  onChange={(e) => handleChange('login_progress_track_color', e.target.value)}
                />
              </div>
            </div>

            <div style={{ marginTop: '1rem' }}>
              <label style={{
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.text.primary,
                marginBottom: '0.5rem',
                display: 'block'
              }}>
                Progress Style
              </label>
              <select
                value={websiteSettings.login_progress_style || 'pill'}
                onChange={(e) => handleChange('login_progress_style', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.md,
                  fontSize: theme.typography.fontSize.base,
                  fontFamily: theme.typography.fontFamily.primary,
                }}
              >
                <option value="pill">Pill</option>
                <option value="flat">Flat</option>
                <option value="thin">Thin</option>
              </select>
            </div>
          </div>

          <ProgressPreview theme={theme}>
            <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
              Live Preview
            </span>
            <PreviewTrack
              theme={theme}
              $style={websiteSettings.login_progress_style || 'pill'}
              $track={websiteSettings.login_progress_track_color || '#d1d5db'}
            >
              <PreviewFill
                $color={websiteSettings.login_progress_color || '#ffffff'}
                style={{ width: '65%' }}
              />
            </PreviewTrack>
            <small style={{ color: theme.colors.text.secondary }}>
              The indicator appears below the Sign In button whenever credentials are being validated.
            </small>
          </ProgressPreview>
        </FormGroup>
      </SettingsCard>

      <SettingsCard theme={theme}>
        <SettingsHeader theme={theme}>
          <SettingsIcon theme={theme}>
            <Settings size={24} />
          </SettingsIcon>
          <SettingsTitle theme={theme}>SEO & Meta Information</SettingsTitle>
        </SettingsHeader>
        
        <FormGroup theme={theme}>
            <Input
            label="Meta Title"
            value={websiteSettings.meta_title || ''}
            onChange={(e) => handleChange('meta_title', e.target.value)}
            placeholder="Enter meta title (for search engines)"
            helpText="This appears in search engine results (50-60 characters recommended)"
            maxLength={60}
          />
          
          <div>
            <label style={{ 
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.text.primary,
              marginBottom: '0.5rem',
              display: 'block'
            }}>
              Meta Description
            </label>
            <TextArea
              theme={theme}
              value={websiteSettings.meta_description || ''}
              onChange={(e) => handleChange('meta_description', e.target.value)}
              placeholder="Enter meta description (for search engines)"
              maxLength={160}
            />
            <small style={{ color: theme.colors.text.secondary, fontSize: theme.typography.fontSize.xs }}>
              {websiteSettings.meta_description?.length || 0}/160 characters. This appears in search engine results.
            </small>
          </div>
          
          <Input
            label="Meta Keywords"
            value={websiteSettings.meta_keywords || ''}
            onChange={(e) => handleChange('meta_keywords', e.target.value)}
            placeholder="keyword1, keyword2, keyword3"
            helpText="Comma-separated keywords for SEO"
          />
        </FormGroup>
        </SettingsCard>
      
      <ButtonGroup theme={theme} style={{ marginTop: '2rem' }}>
        <Button 
          variant="primary" 
          onClick={handleSave} 
          leftIcon={<Save size={16} />}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </ButtonGroup>
    </SettingsContainer>
  );
};

export default SettingsPage;
